import { FC } from 'react';

import { Link } from '@inertiajs/react';
import { Check, X, Trash2 } from 'lucide-react';

import { Button } from '../../../../components/ui/Button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '../../../../components/ui/Card';
import { Routes } from '../../../../models/routes';
import { CartonProduct, OfferProperty } from '../../../../types';

interface Props {
    selectedProduct: CartonProduct | null;
    offerProperties: OfferProperty[];
    amount: string;
    price: string;
    propertySet: Record<string, string>;
    processing: boolean;
    editing: boolean;
    isSubmitDisabled: boolean;
    offerStatus?: 'active' | 'inactive' | 'cancelled';
    onActivate?: () => void;
    onDeactivate?: () => void;
    onDelete?: () => void;
}

export const OfferDetails: FC<Props> = ({
    selectedProduct,
    offerProperties,
    amount,
    price,
    propertySet,
    processing,
    editing,
    isSubmitDisabled,
    offerStatus,
    onActivate,
    onDeactivate,
    onDelete,
}) => {
    const parsedPrice = Number.parseFloat(price);
    const displayPrice = Number.isFinite(parsedPrice) ? parsedPrice : 0;

    return (
        <div>
            <Card>
                <CardHeader>
                    <CardTitle className="text-base sm:text-lg">
                        Підсумок пропозиції
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <div className="flex justify-between text-xs sm:text-sm">
                            <span className="text-muted-foreground">
                                Товар:
                            </span>
                            <span className="font-medium">
                                {selectedProduct?.name}
                            </span>
                        </div>

                        {offerProperties?.map((property) => {
                            const selectedValue = propertySet[property.slug];
                            const selectedOption =
                                property.offer_property_options?.find(
                                    (option) => option.slug === selectedValue,
                                );

                            if (!selectedOption) return null;

                            return (
                                <div
                                    key={property.id}
                                    className="flex justify-between text-xs sm:text-sm"
                                >
                                    <span className="text-muted-foreground">
                                        {property.name}:
                                    </span>
                                    <span>{selectedOption.name}</span>
                                </div>
                            );
                        })}

                        <div className="flex justify-between text-xs sm:text-sm">
                            <span className="text-muted-foreground">
                                Кількість:
                            </span>
                            <span>{amount}</span>
                        </div>
                    </div>

                    <div className="space-y-2 border-t pt-4">
                        <div className="flex justify-between text-xs sm:text-sm">
                            <span className="text-muted-foreground">
                                Ваша ціна:
                            </span>
                            <span className="font-medium">
                                ₴{displayPrice.toFixed(2)}
                            </span>
                        </div>
                    </div>

                    <div className="pt-4">
                        <Button
                            className="h-9 w-full text-xs sm:h-10 sm:text-sm"
                            type="submit"
                            variant="success"
                            disabled={processing || isSubmitDisabled}
                        >
                            {editing
                                ? 'Зберегти пропозицію'
                                : 'Створити пропозицію'}
                        </Button>
                        {!editing && (
                            <p className="mt-2 text-center text-[10px] text-muted-foreground sm:text-xs">
                                Створюючи цю пропозицію, ви погоджуєтеся з
                                нашими{' '}
                                <Link href={Routes.Terms} className="underline">
                                    Умовами обслуговування
                                </Link>{' '}
                                та{' '}
                                <Link
                                    href={Routes.Privacy}
                                    className="underline"
                                >
                                    Політикою продавця
                                </Link>
                                .
                            </p>
                        )}
                    </div>

                    {editing && (
                        <div className="space-y-2 border-t pt-4">
                            <div className="flex gap-2">
                                {offerStatus === 'active' ? (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className="h-9 flex-1 text-xs sm:text-sm"
                                        onClick={onDeactivate}
                                        disabled={processing}
                                    >
                                        <X className="mr-1 h-4 w-4" />
                                        Деактивувати
                                    </Button>
                                ) : (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className="h-9 flex-1 text-xs sm:text-sm"
                                        onClick={onActivate}
                                        disabled={processing}
                                    >
                                        <Check className="mr-1 h-4 w-4" />
                                        Активувати
                                    </Button>
                                )}
                            </div>
                            <Button
                                type="button"
                                variant="destructive"
                                className="h-9 w-full text-xs sm:text-sm"
                                onClick={onDelete}
                                disabled={processing}
                            >
                                <Trash2 className="mr-1 h-4 w-4" />
                                Видалити пропозицію
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};
